import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import {
    IconButton,
    Tooltip
} from "@mui/material";
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { closeModal } from "../../../state/ModalSlice/ModalSlice.ts";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from '@mui/icons-material/Add';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ListIcon from '@mui/icons-material/List';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Info } from '@mui/icons-material';
import { openModal } from '../../../state/ModalSlice/ModalSlice';
import { setFieldsForAddCustomerKYCFieldModal, setFieldsForEditCustomerKYCFieldModal, setFieldsForDeleteCustomerKYCFieldModal } from '../../../state/ArbitrarySlice/ArbitratySlice';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../Config/firebase';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';

export type CustomerKYCFieldType = {
    id: number;
    type: string;
    fieldDescription: string;
    options?: string[];
    isRequired: boolean;
}

export default function CustomerKYCModal() {

    // ------------------------DialogHandlers
    const Dispatch = useAppDispatch();
    const open = useAppSelector((state: RootState) => state.ModalSlice.CustomerKYCModalOpen);
    const serviceProviderId = useAppSelector(selectEffectiveUID);
    
    // Local state for customerKYCFields with snapshot listener
    const [customerKYCFields, setCustomerKYCFields] = useState<CustomerKYCFieldType[]>([]);

    // Set up Firestore snapshot listener
    useEffect(() => {
        if (!serviceProviderId) return;

        const serviceProviderRef = doc(db, 'ServiceProviders', serviceProviderId);
        
        const unsubscribe = onSnapshot(serviceProviderRef, (doc) => {
            if (doc.exists()) {
                const data = doc.data();
                setCustomerKYCFields(data.customerKYCFields || []);
            } else {
                setCustomerKYCFields([]);
            }
        }, (error) => {
            console.error('Error listening to customerKYCFields:', error);
            setCustomerKYCFields([]);
        });

        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, [serviceProviderId]);

    const handleClose = () => {
        Dispatch(closeModal("CustomerKYCModalOpen"))
    };

    const handleAddQuestion = () => {
        Dispatch(setFieldsForAddCustomerKYCFieldModal())
        Dispatch(openModal("AddCustomerKYCFieldModalOpen"))
    };

    const handleEditQuestion = (question: CustomerKYCFieldType) => {
        Dispatch(setFieldsForEditCustomerKYCFieldModal({ question: question }))
        Dispatch(openModal("EditCustomerKYCFieldModalOpen"));
    };

    const handleDeleteClick = (question: CustomerKYCFieldType) => {
        Dispatch(setFieldsForDeleteCustomerKYCFieldModal({ questionId: question.id }))
        Dispatch(openModal("DeleteCustomerKYCFieldModalOpen"))
    }

    return (
        <React.Fragment>
            <Dialog
                sx={{
                    padding: 0,
                    '& .MuiPaper-root': {
                        borderRadius: '10px',
                    },
                }}
                maxWidth="sm"
                fullWidth
                open={open}
                PaperProps={{
                    component: 'div',
                    className: 'relative',
                }}
            >
                <DialogContent sx={{ 
                    paddingX: { xs: "16px", sm: "24px", md: "60px" }, 
                    paddingY: { xs: "30px", sm: "40px", md: "50px" } 
                }} className="bg-white">
                    <div className="w-full max-w-[550px] mx-auto">
                        <div className="header pb-3 sm:pb-[15px] space-y-2">
                            <h2 className="text-lg sm:text-2xl font-semibold text-gray-800 transition-all duration-300">
                                Customer KYC Fields
                            </h2>
                            <p className="text-xs sm:text-sm text-gray-500 font-medium">
                                Manage custom fields for customer information collection
                            </p>
                        </div>

                        <div className="absolute right-2 sm:right-4 top-2 sm:top-4">
                            <IconButton 
                                onClick={handleClose}
                                className="hover:rotate-90 transition-transform duration-300"
                                sx={{
                                    padding: { xs: '4px', sm: '8px' },
                                    '&:hover': {
                                        backgroundColor: 'rgba(0,0,0,0.04)',
                                    }
                                }}
                            >
                                <CloseIcon className="text-gray-500 hover:text-gray-700" sx={{ fontSize: { xs: '18px', sm: '24px' } }} />
                            </IconButton>
                        </div>

                        <div className="pt-0">
                            <div className="w-full">
                                <div className="space-y-6">
                                    <div className="bg-white rounded-xl border border-gray-200">
                                        <div className="p-6">
                                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
                                                <span className="flex items-center gap-2">
                                                    <h3 className="text-base sm:text-lg font-semibold text-gray-800">Custom KYC Questions</h3>
                                                    <Tooltip className='cursor-help' title="Customers will be asked these questions during registration" >
                                                        <Info sx={{ fontSize: { xs: '16px', sm: '20px' } }} />
                                                    </Tooltip>
                                                </span>
                                                <button
                                                    onClick={handleAddQuestion}
                                                    className="group flex items-center gap-2 sm:gap-3 p-2 sm:p-4 rounded-xl border border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/30 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg w-full sm:w-auto"
                                                >
                                                    <div className="p-1.5 sm:p-2.5 rounded-lg bg-blue-50 group-hover:bg-blue-100 transition-colors duration-300">
                                                        <AddIcon sx={{ fontSize: { xs: '18px', sm: '20px' }, color: '#2563eb' }} />
                                                    </div>
                                                    <div className="text-left">
                                                        <span className="block text-xs sm:text-sm font-semibold text-gray-800">
                                                            Add Question
                                                        </span>
                                                        <span className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
                                                            Create custom field
                                                        </span>
                                                    </div>
                                                </button>
                                            </div>

                                            <div className="grid grid-cols-1 gap-4">
                                                {customerKYCFields && customerKYCFields.length > 0 ? (
                                                    customerKYCFields.map((question) => (
                                                        <button
                                                            key={question.id}
                                                            className="group flex items-center gap-2 sm:gap-3 p-2 sm:p-4 rounded-xl border border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/30 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg text-left w-full"
                                                        >
                                                            <div className="p-1.5 sm:p-2.5 rounded-lg bg-blue-50 group-hover:bg-blue-100 transition-colors duration-300 flex-shrink-0">
                                                                {question.type === 'textfield' ? (
                                                                    <TextFieldsIcon sx={{ fontSize: { xs: '18px', sm: '20px' }, color: '#2563eb' }} />
                                                                ) : question.type === 'radio' ? (
                                                                    <RadioButtonCheckedIcon sx={{ fontSize: { xs: '18px', sm: '20px' }, color: '#d97706' }} />
                                                                ) : question.type === 'checkbox' ? (
                                                                    <CheckBoxIcon sx={{ fontSize: { xs: '18px', sm: '20px' }, color: '#16a34a' }} />
                                                                ) : question.type === 'dropdown' ? (
                                                                    <ListIcon sx={{ fontSize: { xs: '18px', sm: '20px' }, color: '#9333ea' }} />
                                                                ) : question.type === 'location' ? (
                                                                    <LocationOnIcon sx={{ fontSize: { xs: '18px', sm: '20px' }, color: '#4f46e5' }} />
                                                                ) : null}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <span className="block text-xs sm:text-sm font-semibold text-gray-800 truncate">
                                                                    {question.fieldDescription}
                                                                </span>
                                                                <div className="flex items-center gap-2 mt-0.5">
                                                                    <span className="text-[10px] sm:text-xs text-gray-500">
                                                                        {question.type}
                                                                    </span>
                                                                    {question.isRequired && (
                                                                        <span className="text-[10px] sm:text-xs text-blue-600 font-medium">
                                                                            Required
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-1 sm:gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex-shrink-0">
                                                                <button
                                                                    className="p-1 sm:p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200 rounded-lg hover:bg-gray-100"
                                                                    aria-label="Edit question"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleEditQuestion(question);
                                                                    }}
                                                                >
                                                                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                                    </svg>
                                                                </button>
                                                                <button
                                                                    className="p-1 sm:p-2 text-gray-400 hover:text-red-600 transition-colors duration-200 rounded-lg hover:bg-red-50"
                                                                    aria-label="Delete question"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDeleteClick(question);
                                                                    }}
                                                                >
                                                                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </button>
                                                    ))
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center py-16 px-4 bg-gray-50 rounded-2xl">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24"
                                                            fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
                                                            strokeLinejoin="round" className="text-gray-400 mb-4">
                                                            <circle cx="12" cy="12" r="10"></circle>
                                                            <line x1="12" y1="16" x2="12" y2="12"></line>
                                                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                                        </svg>
                                                        <p className="text-lg font-medium text-gray-600 mb-2">No KYC Fields Yet</p>
                                                        <p className="text-sm text-gray-500 text-center">
                                                            Start adding custom fields to collect customer information during registration.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
}
